import React, { useState, useMemo, useEffect } from 'react'
import api from '../../services/api.js'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth.js'
import { useProjects } from '../../hooks/useProjects.jsx'
import { useTeam } from '../../hooks/useTeam.js'
import { useTheme } from '../../hooks/useTheme.jsx'
import { mockUsers, mockProjects, userRoles } from '../../data/mockData.js'
import { formatDate } from '../../utils/helpers'
import ProjectModal from './ProjectModal.jsx'
import { Plus, Search, Filter, Trash2, Edit3, CheckCircle, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// Helper to get unique values for filters
const getFilterOptions = (projects, field, label) => {
    const values = [...new Set(projects.map(p => p[field]).filter(Boolean))];
    return [label, ...values];
};

/**
 * Who is on this project, on the card itself.
 *
 * An Admin scanning a page of projects wants to see the people without opening each one. There is
 * rarely room for all of them, so the strip scrolls itself — slowly, and only when the names
 * actually overflow. A strip that animates when everything already fits is just movement.
 *
 * It stops the moment somebody touches it, and does not start again: once a person is reading,
 * moving the text under them is the opposite of helpful.
 *
 * Clicking a name searches for it. That is the same question the name raises — "what else is this
 * person on?" — and it is already answered by the search box above.
 */
function PeopleStrip({ pm, coManagers = [], consultants = [], onPick, isDark }) {
  const ref = React.useRef(null)
  const [paused, setPaused] = React.useState(false)

  React.useEffect(() => {
    const el = ref.current
    if (!el || paused) return
    // Nothing to do when it all fits. Checked on every run rather than once, because the card
    // reflows as the window changes and a strip can stop overflowing.
    if (el.scrollWidth <= el.clientWidth + 4) return
    let dir = 1
    const id = setInterval(() => {
      if (!ref.current) return
      const node = ref.current
      const max = node.scrollWidth - node.clientWidth
      if (node.scrollLeft >= max - 1) dir = -1
      else if (node.scrollLeft <= 0) dir = 1
      node.scrollLeft += dir * 0.6      // slow enough to read while it moves
    }, 40)
    return () => clearInterval(id)
  }, [paused, pm, coManagers.length, consultants.length])

  const people = [
    ...(pm ? [{ name: pm, role: 'PM' }] : []),
    ...coManagers.map(n => ({ name: n, role: 'PM' })),
    ...consultants.map(n => ({ name: n, role: 'Consultant' })),
  ]
  if (people.length === 0) {
    return <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Nobody assigned yet</p>
  }

  return (
    <div
      ref={ref}
      onMouseEnter={() => setPaused(true)}
      onTouchStart={() => setPaused(true)}
      onWheel={() => setPaused(true)}
      className="flex gap-1.5 overflow-x-auto scrollbar-none py-0.5"
      style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      title="Click a name to find their other projects"
    >
      {people.map((p, i) => (
        <button
          key={`${p.role}-${p.name}-${i}`}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onPick?.(p.name) }}
          className={`shrink-0 text-[11px] leading-none pl-1 pr-2.5 py-1 rounded-full border font-medium transition-colors ${
            p.role === 'PM'
              ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
              : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
          }`}
        >
          {/* The role reads as a solid chip inside the pale pill. As a faded prefix it was the
              lightest thing on the card, which is backwards — the role is what makes the name
              mean something. */}
          <span className={`inline-block mr-1.5 px-1.5 py-0.5 rounded-full text-white font-semibold ${
            p.role === 'PM' ? 'bg-indigo-600' : 'bg-emerald-600'
          }`}>{p.role === 'PM' ? 'PM' : 'C'}</span>{p.name}
        </button>
      ))}
    </div>
  )
}

/**
 * Milestones as segments rather than one blue bar.
 *
 * "68%" is a number nobody can act on. "2 of 3 done, 1 in progress" says what is finished, what is
 * moving and what has not started — which is the question a manager actually has.
 *
 * Falls back to the plain percentage when a project has no milestones yet, because segments of
 * nothing would read as a project with no work rather than one not yet broken down.
 */
// The colour of a step: grey = not started, blue = with the consultant, amber = waiting on a manager,
// violet = waiting on Finance, green = paid.
const stageColor = (key) => ({
  paid: 'bg-emerald-100 text-emerald-800', done: 'bg-emerald-100 text-emerald-800',
  pay: 'bg-violet-100 text-violet-800',
  review: 'bg-amber-100 text-amber-800', invoice_approve: 'bg-amber-100 text-amber-800',
  invoice_raise: 'bg-amber-100 text-amber-800', countersign: 'bg-amber-100 text-amber-800',
  accepted: 'bg-amber-100 text-amber-800', unassigned: 'bg-amber-100 text-amber-800',
  invoice_confirm: 'bg-blue-100 text-blue-800', contract_sign: 'bg-blue-100 text-blue-800',
  offered: 'bg-blue-100 text-blue-800', not_started: 'bg-blue-100 text-blue-800',
}[key] || 'bg-gray-100 text-gray-700')
const stepBar = (key) => ({ paid: 'bg-emerald-500', pay: 'bg-violet-500', review: 'bg-amber-400', invoice_approve: 'bg-amber-400',
  invoice_raise: 'bg-amber-400', countersign: 'bg-amber-300', invoice_confirm: 'bg-blue-400' }[key] || null)

function MilestoneProgress({ total = 0, done = 0, inFlight = 0, progress = 0, next, nextDue, nextPerson, isDark, steps = [] }) {
  // EACH MILESTONE'S OWN STEP, when the server sent them: "waiting for review", "waiting for Finance"
  // -- not "0 of 3 done, 100% left" while the work sat submitted.
  if (steps.length) {
    const track = isDark ? 'bg-gray-700' : 'bg-gray-200'
    const pct = Math.round(100 * steps.reduce((a, st) => a + (st.weight || 0), 0) / steps.length)
    const paid = steps.filter(st => st.key === 'paid').length
    return (
      <>
        <div className="flex justify-between items-center mb-2 gap-2">
          <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            <b>{paid} of {steps.length} milestones paid</b>
          </span>
          <span className={`text-xs font-semibold shrink-0 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{pct}% along</span>
        </div>
        <div className="flex gap-1 w-full">
          {steps.map(st => (
            <div key={st.id} title={`${st.title}: ${st.label}`} className={`h-2 flex-1 rounded-full ${track} overflow-hidden`}>
              <div className={`h-2 ${stepBar(st.key) || 'bg-blue-300'}`} style={{ width: `${Math.max(st.weight > 0 ? 8 : 0, Math.round((st.weight || 0) * 100))}%` }} />
            </div>
          ))}
        </div>
        <ul className="mt-2 space-y-1">
          {steps.slice(0, 4).map(st => (
            <li key={st.id} className="flex items-center gap-2 text-[11px] min-w-0">
              <span className={`shrink-0 px-1.5 py-0.5 rounded ${stageColor(st.key)}`}>{st.label}</span>
              <span className={`truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{st.title}</span>
            </li>
          ))}
          {steps.length > 4 && <li className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>+{steps.length - 4} more</li>}
        </ul>
      </>
    )
  }
  const track = isDark ? 'bg-gray-700' : 'bg-gray-200'
  if (!total) {
    return (
      <>
        <div className="flex justify-between items-center mb-2">
          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No milestones yet</span>
          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{progress}%</span>
        </div>
        <div className={`w-full rounded-full h-2 ${track}`}>
          <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      </>
    )
  }
  const notStarted = Math.max(0, total - done - inFlight)
  const remainingPct = Math.round(100 * (total - done) / total)
  return (
    <>
      <div className="flex justify-between items-center mb-2 gap-2">
        <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'} truncate`}>
          <b>{done} of {total} milestones</b> done
          {inFlight > 0 && <> · <span className="text-amber-600">{inFlight} in progress</span></>}
          {notStarted > 0 && <> · {notStarted} not started</>}
        </span>
        <span className={`text-xs font-semibold shrink-0 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
          {remainingPct}% left
        </span>
      </div>
      {/* One segment per milestone, so the bar is countable. Gaps make them separable at a glance. */}
      <div className="flex gap-1 w-full">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-2 flex-1 rounded-full transition-colors ${
              i < done ? 'bg-emerald-500'
                : i < done + inFlight ? 'bg-amber-400'
                : track
            }`}
          />
        ))}
      </div>
      {/* WHAT IS NEXT, BY NAME. "1 not started" says how much is left; it does not say what it
          is, and that is the question the card was being opened to answer. */}
      {next && (
        <p className={`text-[11px] mt-1.5 truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {/* WHOSE TURN, NOT JUST WHAT. An Admin reading this is deciding whether to chase
              somebody, and a milestone title alone does not say who. Nobody assigned yet is
              worth saying out loud — it is the reason a milestone sits still. */}
          Next: <span className={isDark ? 'text-gray-200' : 'text-gray-700'}>{next}</span>
          {nextPerson
            ? <> — <span className="text-emerald-700 font-medium">{nextPerson}</span></>
            : <> — <span className="text-amber-600">nobody assigned</span></>}
          {nextDue && <> · due {formatDate(nextDue)}</>}
        </p>
      )}
    </>
  )
}

function ProjectList() {
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('projects')
  const { user } = useAuth()
  const { projects, loading, createProject, updateProject, deleteProject, approveProject, rejectProject, pendingProjectsForUser } = useProjects()
  const { members } = useTeam()
  const { isDark } = useTheme() // Use manual theme check

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingProject, setEditingProject] = useState(null)
  // v3.2: opening the create modal pre-set to build a sub-project under a parent
  // (deep-linked from a parent project's "Create sub-project" button: /projects?subOf=<id>).
  const [subParentId, setSubParentId] = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const subOf = searchParams.get('subOf')
    if (!subOf) return
    setEditingProject(null)
    setSubParentId(subOf)
    setIsModalOpen(true)
    const next = new URLSearchParams(searchParams); next.delete('subOf'); setSearchParams(next, { replace: true })
  }, [searchParams])

  // Per-project action dots — fetch deliverable state for PM/Admin
  const [projectDots, setProjectDots] = useState({})
  useEffect(() => {
    const isPMorAdmin = user?.role === 'Project Manager' || user?.role === 'Admin'
    if (!isPMorAdmin || !projects.length) return
    let cancelled = false
    Promise.all(projects.filter(p => p.status === 'In Progress').map(async p => {
      try {
        const dl = await api.get(`/deliverables/project/${p.id}`).catch(() => ({ data: [] }))
        const deliverables = dl.data || []
        const hasSubmitted = deliverables.some(d => ['Submitted', 'Revision Requested'].includes(d.status))
        const ms = await api.get(`/projects/${p.id}/milestones/`).catch(() => ({ data: [] }))
        const milestones = ms.data || []
        const needsInvoiceApproval = milestones.some(m =>
          m.consultant_invoice_confirmed &&
          !(user?.role === 'Admin' ? m.admin_invoice_approved : m.pm_invoice_approved)
        )
        if (hasSubmitted) return [p.id, { color: 'bg-amber-400', label: 'Review deliverable', pulse: true }]
        if (needsInvoiceApproval) return [p.id, { color: 'bg-amber-400', label: 'Approve invoice', pulse: true }]
        return [p.id, null]
      } catch { return [p.id, null] }
    })).then(rows => {
      if (cancelled) return
      const next = {}
      for (const [id, dot] of rows) if (dot) next[id] = dot
      setProjectDots(next)
    })
    return () => { cancelled = true }
  }, [projects.map(p => p.id).join(','), user?.role])

  // State for new filters
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('All Status')
  // Card view stays the default; list view groups each programme with its sub-projects underneath.
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('projects-view') || 'card')
  const [sortBy, setSortBy] = useState('newest')
  const setView = (v) => { setViewMode(v); try { localStorage.setItem('projects-view', v) } catch {} }

  // A programme and its sub-projects share one colour, so the relationship is visible at a glance
  // instead of having to read every card. Keyed off the PARENT id (or the project's own id when it
  // is a parent), so a family always matches. Deterministic — the same project keeps its colour.
  const PROGRAMME_COLOURS = [
    { bar: 'bg-indigo-500', chip: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
    { bar: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
    { bar: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
    { bar: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
    { bar: 'bg-sky-500', chip: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500' },
    { bar: 'bg-violet-500', chip: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
    { bar: 'bg-teal-500', chip: 'bg-teal-50 text-teal-700 border-teal-200', dot: 'bg-teal-500' },
  ]
  const familyId = (p) => p?.parentProjectId || p?.id || 0
  // Colour is assigned by the family's POSITION among the programmes on screen, not by
  // `id % palette`. That earlier arithmetic gave two programmes whose ids differ by exactly the
  // palette size the same colour — which is what made every card look identical.
  const familyOrder = useMemo(() => {
    const seen = []
    for (const p of projects) {
      const fid = familyId(p)
      const isFamily = !!p.parentProjectId || projects.some(x => x.parentProjectId === p.id)
      if (isFamily && !seen.includes(fid)) seen.push(fid)
    }
    return seen
  }, [projects])
  const programmeColour = (p) => {
    const idx = familyOrder.indexOf(familyId(p))
    return PROGRAMME_COLOURS[(idx < 0 ? 0 : idx) % PROGRAMME_COLOURS.length]
  }
  // Only colour projects that are actually part of a family — a standalone project gets no stripe,
  // so the colour always means "this belongs to a programme" rather than being decoration.
  const inFamily = (p) => !!p?.parentProjectId || projects.some(x => x.parentProjectId === p?.id)
  const [categoryFilter, setCategoryFilter] = useState('All Categories')

  const allStatuses = useMemo(() => getFilterOptions(projects, 'status', 'All Status'), [projects]);
  const allCategories = useMemo(() => getFilterOptions(projects, 'category', 'All Categories'), [projects]);

  // Filter logic now uses the 'projects' from the hook
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      const matchesStatus = statusFilter === 'All Status' || project.status === statusFilter
      const matchesCategory = true
      const q = searchTerm.toLowerCase()
      // PEOPLE ARE SEARCHABLE TOO. "Which projects is Dhiraj on?" is the question an Admin
      // actually asks, and before this the only way to answer it was to open every project.
      // The description stays searchable even though the card no longer shows it — somebody
      // looking for a phrase they remember writing should still find it.
      const people = [
        project.project_manager_name || project.projectManagerName || '',
        ...(project.coManagerNames || []),
        ...(project.consultantNames || []),
      ].join(' ').toLowerCase()

      const matchesSearch = searchTerm === '' ||
        project.name.toLowerCase().includes(q) ||
        (project.description || '').toLowerCase().includes(q) ||
        // search by OUR canonical Project ID, and also the client's own reference
        (project.project_number || '').toLowerCase().includes(q) ||
        (project.external_ref || '').toLowerCase().includes(q) ||
        people.includes(q)

      // Role filtering is already handled by the useProjects hook,
      // but we apply the UI filters on top.
      return matchesStatus && matchesCategory && matchesSearch
    })
    // Sorted by start date. No creation timestamp is stored on a project, so id (which increments
    // in creation order) breaks ties and covers projects with no start date set.
    .sort((a, b) => {
      const da = a.startDate || a.start_date || ''
      const db_ = b.startDate || b.start_date || ''
      if (da !== db_) {
        if (!da) return 1
        if (!db_) return -1
        return sortBy === 'oldest' ? (da < db_ ? -1 : 1) : (da > db_ ? -1 : 1)
      }
      return sortBy === 'oldest' ? a.id - b.id : b.id - a.id
    })
  }, [projects, searchTerm, statusFilter, categoryFilter, sortBy])

  // Card view: a programme is followed immediately by its own sub-projects, under a full-width
  // header. Position is what actually groups a family — colour alone can't, because the grid wraps
  // and related cards end up far apart. Standalone projects follow afterwards, ungrouped.
  const orderedCards = useMemo(() => {
    const out = []
    const placed = new Set()
    for (const p of filteredProjects) {
      if (placed.has(p.id)) continue
      if (p.parentProjectId) continue                     // children are emitted with their parent
      const kids = filteredProjects.filter(x => x.parentProjectId === p.id)
      if (kids.length) {
        out.push({ type: 'header', key: `h${p.id}`, project: p, count: kids.length })
        out.push({ type: 'card', key: `c${p.id}`, project: p })
        placed.add(p.id)
        for (const k of kids) { out.push({ type: 'card', key: `c${k.id}`, project: k }); placed.add(k.id) }
      }
    }
    // Everything else: standalone projects, plus any child whose parent is filtered out.
    const rest = filteredProjects.filter(p => !placed.has(p.id))
    if (rest.length && out.length) out.push({ type: 'header', key: 'h-other', project: null, count: rest.length })
    for (const p of rest) { out.push({ type: 'card', key: `c${p.id}`, project: p }); placed.add(p.id) }
    return out
  }, [filteredProjects])

  const handleCreateProject = async (projectData) => {
    // editingProject is set only when the Edit action was used → an update, not a create.
    if (editingProject) {
      await updateProject(editingProject.id, {
        name: projectData.name,
        description: projectData.description,
        total_budget: parseFloat(projectData.totalBudget || projectData.total_budget || 0),
        start_date: projectData.startDate || projectData.start_date,
        end_date: projectData.endDate || projectData.end_date,
        priority: projectData.priority,
        category: projectData.category,
        risk_level: projectData.riskLevel || projectData.risk_level,
        budget_type: projectData.budgetType || projectData.budget_type,
        job_title: (projectData.jobTitle || '').trim() || null,
        purpose_of_position: (projectData.purposeOfPosition || '').trim() || null,
        // Admin can hand the project to a different PM from the Assign step.
        ...(projectData.projectManagerId ? { project_manager_id: parseInt(projectData.projectManagerId) } : {}),
        ...(Array.isArray(projectData.teamMemberIds) ? { team_member_ids: projectData.teamMemberIds } : {}),
        // v2 nested projects: move under (or detach from) a parent
        parent_project_id: projectData.parentProjectId ? parseInt(projectData.parentProjectId) : null,
      })
      // Persist milestone edits: update existing ones (locked/accepted ones are refused by
      // the backend and skipped), create any newly added ones. Nothing is deleted here.
      const isHybrid = (projectData.budgetType || projectData.budget_type) === 'Hybrid'
      const isActual = (projectData.budgetType || projectData.budget_type) === 'Actual'
      for (const m of (projectData.milestones || [])) {
        const title = (m.description || m.title || '').trim()
        if (!title) continue
        const actualMs = isActual || (isHybrid && m.mode === 'actual')
        const amt = actualMs ? 0 : (m.amount ? parseFloat(m.amount) : 0)
        try {
          if (m.id) {
            if (m.locked) continue // agreed scope — backend refuses edits anyway
            await api.put(`/projects/milestones/${m.id}`, null, {
              params: { title, payment_amount: amt, due_date: m.deadline || undefined },
            })
          } else {
            await api.post(`/projects/${editingProject.id}/milestones/`, {
              title,
              project_id: editingProject.id,
              payment_mode: actualMs ? 'Actual' : 'Fixed',
              payment_amount: amt,
              amount_released_on_completion: amt,
              due_date: m.deadline || null,
            })
          }
        } catch { /* non-fatal — other milestones still sync */ }
      }
      // Upload any newly-attached Project Files into the project's document store.
      for (const pf of (projectData.files || [])) {
        if (!pf?.file) continue  // only freshly-picked files carry a raw File object
        try {
          const fd = new FormData()
          fd.append('title', pf.name || pf.file.name || 'Project file')
          fd.append('file', pf.file)
          await api.post(`/projects/${editingProject.id}/documents/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        } catch { /* non-fatal */ }
      }
    } else {
      // Duplicate-name and consultant-required checks now happen inside the wizard
      // (on Next / before Create), so by the time we get here the data is valid.
      const result = await createProject(projectData)
      // If the consultant couldn't be offered milestones (e.g. not active yet), tell the PM.
      if (result?.warning) {
        alert(`⚠ ${result.warning}`)
      }
    }
    setIsModalOpen(false)
    setEditingProject(null)
  }

  // TWO WAYS TO START, ASKED BEFORE THE FORM OPENS.
  //
  // The templates were already inside the form, three steps up from where the milestones land,
  // and somebody who did not know they existed never scrolled past the TOR box to find them. A
  // choice at the door is where the decision actually gets made.
  const [chooserOpen, setChooserOpen] = useState(false)
  const [templates, setTemplates] = useState([])
  const [presetTemplate, setPresetTemplate] = useState(null)

  const handleOpenNewProjectModal = () => {
    setEditingProject(null)
    setPresetTemplate(null)
    setChooserOpen(true)
    // Fetched when the choice is offered rather than on every visit to this page. An empty
    // answer is normal and simply leaves the second option saying there are none yet.
    api.get('/templates/?kind=project')
      .then(r => setTemplates(r.data?.items || []))
      .catch(() => setTemplates([]))
  }

  const startBlank = () => { setChooserOpen(false); setIsModalOpen(true) }
  const startFromTemplate = (tmpl) => {
    setPresetTemplate(tmpl)
    setChooserOpen(false)
    setIsModalOpen(true)
  }

  const handleApprove = async (projectId, projectName) => {
    if (window.confirm(`Approve project "${projectName}"? This will set it to "In Progress" and notify the PM.`)) {
      const result = await approveProject(projectId)
      if (!result.success) alert('Failed to approve: ' + result.error)
    }
  }

  const handleReject = async (projectId, projectName) => {
    const reason = window.prompt(`Why are you rejecting "${projectName}"? (optional)`)
    if (reason !== null) { // null means user clicked Cancel
      const result = await rejectProject(projectId, reason)
      if (!result.success) alert('Failed to reject: ' + result.error)
    }
  }

  const handleDelete = async (projectId, projectName) => {
    if (window.confirm(`⚠️ Are you sure you want to permanently delete "${projectName}"? This cannot be undone.`)) {
      const result = await deleteProject(projectId)
      if (!result.success) alert('Failed to delete: ' + result.error)
    }
  }

  const handleEdit = (project) => {
    setEditingProject(project)
    setIsModalOpen(true)
  }

  // (Your existing color functions)
  const getStatusColor = (status) => {
    switch (status) {
      case 'In Progress': return 'bg-blue-100 text-blue-800'
      case 'Completed': return 'bg-green-100 text-green-800'
      case 'Planning': return 'bg-yellow-100 text-yellow-800'
      case 'Pending Approval': return 'bg-amber-100 text-amber-800'
      case 'Rejected': return 'bg-red-100 text-red-800'
      case 'On Hold': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800 border-red-200'
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'Low': return 'bg-green-100 text-green-800 border-green-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  // Define dynamic classes based on isDark
  const containerClass = `p-6 space-y-6 min-h-full ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`
  const titleClass = `text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`

  const inputClass = `w-full pl-10 pr-4 py-2.5 rounded-full shadow-sm text-sm transition-all duration-200 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ${isDark
    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 hover:border-gray-600'
    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 hover:border-gray-300'
    }`

  const iconClass = `absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${isDark ? 'text-gray-500 group-hover:text-blue-400' : 'text-gray-400 group-hover:text-blue-500'
    }`

  const filterBtnClass = `p-2.5 border rounded-full shadow-sm transition-all duration-200 ${isDark
    ? 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-blue-400'
    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-blue-600'
    }`

  const selectClass = `appearance-none pl-4 pr-10 py-2.5 border rounded-full shadow-sm text-sm focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all duration-200 cursor-pointer ${isDark
    ? 'bg-gray-800 border-gray-700 text-white hover:border-gray-600'
    : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
    }`

  const cardClass = `rounded-lg shadow-md border hover:shadow-lg transition-shadow overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`

  const textPrimary = isDark ? 'text-white' : 'text-gray-900'
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-600'

  return (
    <div className={containerClass}>
      {/* NEW header based on the image */}
      {/* NEW header based on the image */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h1 className={titleClass}>{t('list.my_projects')}</h1>
          {/* New Project Button - visible on mobile */}
          {(user?.role === userRoles.ADMIN || user?.role === userRoles.PROJECT_MANAGER) && (
            <button
              onClick={handleOpenNewProjectModal}
              className="flex items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors text-sm sm:text-base whitespace-nowrap"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden xs:inline">{t('list.new_project')}</span>
              <span className="xs:hidden">New</span>
            </button>
          )}
        </div>

        {/* Search & Filters Container: Stack on mobile, Row on desktop */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Bar - Full width on mobile, Grows on desktop */}
          <div className="relative w-full sm:w-auto sm:flex-grow sm:max-w-md group order-1">
            <input
              type="text"
              placeholder={t('list.ph_search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${inputClass} border`}
            />
            <Search className={iconClass} />
          </div>

          {/* Filters Wrapper - Full width flex row on mobile, Auto width on desktop */}
          <div className="flex items-center gap-2 w-full sm:w-auto order-2">

            {/* Card / List view toggle — list view groups sub-projects under their programme. */}
            <div className={`flex rounded-lg border shrink-0 overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
              <button
                type="button"
                onClick={() => setView('card')}
                title={t('list.t_card_view')}
                className={`px-2.5 py-2 text-xs font-medium ${viewMode === 'card'
                  ? 'bg-blue-600 text-white'
                  : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >▦ Cards</button>
              <button
                type="button"
                onClick={() => setView('list')}
                title={t('list.t_list_view')}
                className={`px-2.5 py-2 text-xs font-medium ${viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >☰ List</button>
            </div>

            {/* Sort by date */}
            <div className="relative flex-1 sm:flex-none">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                title={t('list.t_sort_start')}
                className={`${selectClass} w-full`}
              >
                <option value="newest">{t('list.newest_first')}</option>
                <option value="oldest">{t('list.oldest_first')}</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Status Filter - Flex 1 on mobile to share space */}
            <div className="relative flex-1 sm:flex-none">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`${selectClass} w-full`}
              >
                {allStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Pending Approvals Banner (Admin only) */}
      {pendingProjectsForUser && pendingProjectsForUser.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⏳</span>
            <div>
              <p className="font-semibold text-amber-800">
                You have {pendingProjectsForUser.length} project{pendingProjectsForUser.length > 1 ? 's' : ''} currently pending approval.
              </p>
              <p className="text-sm text-amber-600">{t('list.review_and_decide')}</p>
            </div>
          </div>
          <Link to="/projects" className="px-4 py-2 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 transition-colors">
            View Projects
          </Link>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className={`mt-4 ${textSecondary}`}>{t('list.loading')}</p>
        </div>
      )}

      {/* ── LIST VIEW — each programme followed by its sub-projects, sharing one colour ──────── */}
      {!loading && viewMode === 'list' && filteredProjects.length > 0 && (() => {
        // Group: a parent is followed immediately by its children. Children whose parent is not in
        // the filtered set still appear on their own, so filtering never hides a project entirely.
        const shown = new Set()
        const rows = []
        for (const p of filteredProjects) {
          if (p.parentProjectId && filteredProjects.some(x => x.id === p.parentProjectId)) continue
          rows.push({ p, depth: 0 })
          shown.add(p.id)
          for (const c of filteredProjects.filter(x => x.parentProjectId === p.id)) {
            rows.push({ p: c, depth: 1 })
            shown.add(c.id)
          }
        }
        for (const p of filteredProjects) if (!shown.has(p.id)) rows.push({ p, depth: 0 })

        return (
          <div className={`${cardClass} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ minWidth: 760 }}>
                <thead>
                  <tr className={`${isDark ? 'bg-gray-800/60' : 'bg-gray-50'} text-left`}>
                    <th className={`px-4 py-2.5 text-[11px] font-bold uppercase tracking-wide ${textSecondary}`}>{t('list.col_project')}</th>
                    <th className={`px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide ${textSecondary}`}>{tc('labels.status')}</th>
                    <th className={`px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide ${textSecondary}`}>{t('list.col_start')}</th>
                    <th className={`px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide ${textSecondary}`}>End</th>
                    <th className={`px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide text-right ${textSecondary}`}>{tc('labels.budget')}</th>
                    <th className={`px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide text-right ${textSecondary}`}>{t('list.col_progress')}</th>
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ p, depth }) => {
                    const fam = inFamily(p) ? programmeColour(p) : null
                    const childCount = projects.filter(x => x.parentProjectId === p.id).length
                    return (
                      <tr key={p.id} className={`border-t ${isDark ? 'border-gray-700 hover:bg-gray-800/40' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2 min-w-0">
                            {/* Family colour stripe — same colour for a programme and its children */}
                            <span className={`w-1 h-8 rounded shrink-0 ${fam ? fam.bar : 'bg-transparent'}`} />
                            {depth === 1 && <span className="text-gray-300 font-mono select-none shrink-0">└─</span>}
                            <div className="min-w-0">
                              <Link to={`/projects/${p.id}`} className={`font-medium hover:underline ${textPrimary}`}>
                                {p.name}
                              </Link>
                              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                {(p.project_number || p.projectNumber) && (
                                  <span className="font-mono text-[10px] text-gray-400">{p.project_number || p.projectNumber}</span>
                                )}
                                {depth === 1 && fam && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${fam.chip}`}>sub-project</span>
                                )}
                                {childCount > 0 && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${fam ? fam.chip : ''}`}>
                                    📁 {childCount} sub-project{childCount > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full ${getStatusColor(p.status)}`}>{p.status}</span>
                        </td>
                        <td className={`px-3 py-2.5 text-xs ${textSecondary}`}>{formatDate(p.startDate || p.start_date) || '—'}</td>
                        <td className={`px-3 py-2.5 text-xs ${textSecondary}`}>{formatDate(p.endDate || p.end_date) || '—'}</td>
                        <td className={`px-3 py-2.5 text-xs text-right ${textPrimary}`}>${Number(p.totalBudget || p.total_budget || 0).toLocaleString()}</td>
                        <td className={`px-3 py-2.5 text-xs text-right ${textSecondary}`}>{p.progress || 0}%</td>
                        <td className="px-3 py-2.5 text-right">
                          <Link to={`/projects/${p.id}`} className="text-xs text-blue-600 hover:underline whitespace-nowrap">{t('list.view_arrow')}</Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      })()}

      {/* Projects Grid (Uses updated filteredProjects) */}
      {!loading && viewMode === 'card' && filteredProjects.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {orderedCards.map((item) => {
            // Full-width band that opens a family (or the "other projects" run).
            if (item.type === 'header') {
              const hc = item.project ? programmeColour(item.project) : null
              return (
                <div key={item.key} className="col-span-full flex items-center gap-2 mt-2 first:mt-0">
                  {hc && <span className={`w-2.5 h-2.5 rounded-full ${hc.dot}`} />}
                  <span className={`text-[11px] font-bold uppercase tracking-wider ${textSecondary}`}>
                    {item.project
                      ? `Programme · ${item.project.name}`
                      : 'Other projects'}
                  </span>
                  {item.project && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${hc.chip}`}>
                      {item.count} sub-project{item.count > 1 ? 's' : ''}
                    </span>
                  )}
                  <span className={`flex-1 h-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                </div>
              )
            }
            const project = item.project
            const projectDot = projectDots[project.id] || null
            // Countdown calculation: days until end_date (restored from old repo)
            const endDate = project.end_date || project.endDate;
            const daysLeft = endDate
              ? Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24))
              : null;
            // WORK THAT ENDED BEFORE IT WAS EVER ENTERED. A project recorded in ProTrack after
            // its end date had already passed is a HISTORY of work, not work anybody is late on.
            // Calling that "1030 days overdue" in red made the loudest thing on the card a demand
            // nobody can act on — you cannot deliver something that was due in 2023. Real overdue
            // means a project that was live when it was entered and has since slipped, and that
            // signal is worth keeping loud, so the two are separated rather than softened.
            const createdAt = project.createdAt || project.created_at;
            const isHistorical = endDate && createdAt && new Date(endDate) < new Date(createdAt);
            const countdownColor = isHistorical
              ? 'bg-slate-100 text-slate-600 border border-slate-200'
              : daysLeft === null
                ? 'bg-gray-100 text-gray-600'
                : daysLeft < 0 ? 'bg-red-200 text-red-900'
                  : daysLeft <= 2 ? 'bg-red-100 text-red-800'
                    : daysLeft <= 7 ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800';
            const countdownLabel = isHistorical
              ? `Past project · ended ${formatDate(endDate)}`
              : daysLeft === null
                ? 'No end date'
                : daysLeft < 0 ? `${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} overdue`
                  : daysLeft === 0 ? 'Due today'
                    : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`;

            // Family colour: a programme and its sub-projects carry the same stripe, so the
            // relationship reads at a glance instead of having to check each card's badge.
            const fam = inFamily(project) ? programmeColour(project) : null

            return (
            <div key={item.key} className={`${cardClass} ${projectDot?.pulse ? 'ring-1 ring-amber-300' : ''} relative overflow-hidden`}>
              {fam && <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${fam.bar}`} aria-hidden="true" />}
              <div className="p-6">
                {/* Action dot banner — guides PM/Admin to the right action */}
                {projectDot && (
                  <div className={`flex items-center gap-1.5 text-xs font-semibold text-white px-2.5 py-1 rounded-lg mb-3 w-fit ${projectDot.color} ${projectDot.pulse ? 'animate-pulse' : ''}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-white/70" />
                    {projectDot.label} — Click View Details
                  </div>
                )}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold mb-1 ${textPrimary}`}>{project.name}</h3>
                    {/* Our canonical Project ID — the reference people quote in emails, contracts
                        and invoices, so it belongs on the card, not only on the detail page. */}
                    {(project.project_number || project.projectNumber) && (
                      <p className="mb-2">
                        <span
                          title={t('list.t_project_id')}
                          className={`font-mono text-[10px] px-1.5 py-0.5 rounded border ${
                            isDark
                              ? 'bg-gray-800 text-gray-300 border-gray-700'
                              : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                        >
                          {project.project_number || project.projectNumber}
                        </span>
                      </p>
                    )}
                    {/* v2 nested projects: show parent / sub-project relationship */}
                    {(() => {
                      const parent = project.parentProjectId ? projects.find(p => p.id === project.parentProjectId) : null
                      // The children are already in the loaded list — no extra fetch needed. Note
                      // the list is permission-scoped server-side, so this only ever shows the
                      // sub-projects THIS user may see (sub-projects are compartmentalised).
                      const children = projects.filter(p => p.parentProjectId === project.id)
                      const childCount = children.length
                      return (
                        <div className="mb-2">
                        <div className="flex flex-wrap gap-1.5">
                          {parent && (
                            <Link to={`/projects/${parent.id}`} onClick={e => e.stopPropagation()}
                              className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100">
                              ↳ Sub-project of {parent.name}{(parent.project_number || parent.projectNumber) ? ` (${parent.project_number || parent.projectNumber})` : ''}
                            </Link>
                          )}
                          {childCount > 0 && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                              📁 {childCount} sub-project{childCount > 1 ? 's' : ''}
                            </span>
                          )}
                          {/* v3.2: a promoted consultant leads projects where they're the PM */}
                          {user?.role === userRoles.CONSULTANT && (project.projectManagerId === user?.id || project.projectManagerId === user?.user_id) && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200">
                              ⭐ You lead this
                            </span>
                          )}
                        </div>
                        {/* Branch list so a program shows WHICH sub-projects it has, not just how
                            many — mirrors the tree on the project's Overview tab. */}
                        {childCount > 0 && (
                          <div className="mt-1.5 pl-1">
                            {children.map((c, idx) => (
                              <div key={c.id} className="flex items-center gap-1.5 text-[11px] leading-5">
                                <span className="text-purple-300 font-mono select-none">
                                  {idx === children.length - 1 ? '└─' : '├─'}
                                </span>
                                <Link to={`/projects/${c.id}`} onClick={e => e.stopPropagation()}
                                  className="text-purple-700 hover:underline truncate">
                                  {c.name}
                                </Link>
                                {(c.project_number || c.projectNumber) && (
                                  <span className="font-mono text-[10px] text-gray-400 shrink-0">
                                    {c.project_number || c.projectNumber}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        </div>
                      )
                    })()}
                    {/* The description used to sit here. It was the least useful thing on the card
                        — usually a paragraph truncated to two lines, readable in full one click
                        away. Replaced by when the project entered ProTrack, which nothing else on
                        this screen answers. */}
                    <p className={`text-xs ${textSecondary}`}>
                      {project.createdAt
                        ? <>Created {formatDate(project.createdAt)}</>
                        : <span className="opacity-60">Created date not recorded</span>}
                    </p>
                  </div>
                  <div className="ml-4 flex flex-col items-end space-y-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium text-right max-w-[15rem] ${project.stage && project.stage.key !== 'status' ? stageColor(project.stage.key) : getStatusColor(project.status)}`}
                          title={project.stage?.turn ? `Waiting for: ${project.stage.turn}` : project.status}>
                      {project.stage && project.stage.key !== 'status' ? project.stage.label : project.status}
                      {project.stage?.turn && project.stage.key !== 'status' && <span className="block text-[10px] font-normal opacity-80">Next: {project.stage.turn}</span>}
                    </span>
                    {/* Priority is internal management info — hidden from Consultants */}
                    {user?.role !== userRoles.CONSULTANT && (
                      <span className={`text-xs px-2 py-1 rounded-full border font-medium ${getPriorityColor(project.priority)}`}>
                        {project.priority}
                      </span>
                    )}
                    {/* Countdown badge — restored from old mountainside repo */}
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${countdownColor}`} title={endDate ? `Ends ${formatDate(endDate)}` : ''}>
                      {countdownLabel}
                    </span>
                  </div>
                </div>

                <div className="mb-3">
                  <MilestoneProgress
                    total={project.milestonesTotal}
                    done={project.milestonesDone}
                    inFlight={project.milestonesInFlight}
                    steps={project.milestoneSteps}
                    progress={project.progress}
                    next={project.nextMilestone}
                    nextDue={project.nextMilestoneDue}
                    nextPerson={project.nextMilestoneConsultant}
                    isDark={isDark}
                  />
                </div>

                {/* Who is on it. Consultants are not held on the project — they are on its
                    milestones — so this is the only place a card can show them. */}
                <div className="mb-4">
                  <PeopleStrip
                    pm={project.project_manager_name || project.projectManagerName}
                    coManagers={project.coManagerNames}
                    consultants={project.consultantNames}
                    onPick={(name) => setSearchTerm(name)}
                    isDark={isDark}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  {/* Whole-project Budget/Spent is PM/Admin/Finance info — a Consultant sees
                      their own milestone's figures on the project's Budget tab instead. */}
                  {user?.role !== userRoles.CONSULTANT && (
                    <>
                      <div>
                        <p className={textSecondary}>{tc('labels.budget')}</p>
                        <p className={`font-semibold ${textPrimary}`}>${(project.approvedBudget || project.total_budget || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className={textSecondary}>{t('list.col_spent')}</p>
                        <p className={`font-semibold ${textPrimary}`}>${(project.spentBudget || project.spent_budget || 0).toLocaleString()}</p>
                      </div>
                    </>
                  )}
                  <div>
                    <p className={textSecondary}>{tc('labels.start_date')}</p>
                    <p className={`font-semibold ${textPrimary}`}>{project.start_date ? formatDate(project.start_date) : 'N/A'}</p>
                  </div>
                  <div>
                    <p className={textSecondary}>{tc('labels.end_date')}</p>
                    <p className={`font-semibold ${textPrimary}`}>{project.end_date ? formatDate(project.end_date) : 'N/A'}</p>
                  </div>
                </div>

                {/* Action Buttons — wrap so they never overflow the card at any zoom */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-gray-100">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Approve/Reject - Admin only, for Pending projects */}
                    {user?.role === userRoles.ADMIN && project.status === 'Pending Approval' && (
                      <>
                        <button
                          onClick={() => handleApprove(project.id, project.name)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 text-xs font-semibold rounded-lg hover:bg-green-100 transition-colors border border-green-200"
                          title={t('list.t_approve')}
                        >
                          <CheckCircle className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button
                          onClick={() => handleReject(project.id, project.name)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 text-xs font-semibold rounded-lg hover:bg-red-100 transition-colors border border-red-200"
                          title={t('list.t_reject')}
                        >
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </button>
                      </>
                    )}

                    {/* Edit - PM (own projects) or Admin */}
                    {(user?.role === userRoles.ADMIN || (user?.role === userRoles.PROJECT_MANAGER)) && (
                      <button
                        onClick={() => handleEdit(project)}
                        className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors border ${isDark ? 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'}`}
                        title={t('list.t_edit')}
                      >
                        <Edit3 className="w-3.5 h-3.5" /> Edit
                      </button>
                    )}

                    {/* Delete - Admin only */}
                    {user?.role === userRoles.ADMIN && (
                      <button
                        onClick={() => handleDelete(project.id, project.name)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 text-xs font-semibold rounded-lg hover:bg-red-100 transition-colors border border-red-200"
                        title={t('list.t_delete')}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    )}
                  </div>

                  <Link to={`/projects/${project.id}`} className="shrink-0 whitespace-nowrap px-4 py-2 bg-blue-600 text-white text-sm rounded-lg shadow-sm hover:bg-blue-700 transition-colors">
                    View details
                  </Link>
                </div>
              </div>
            </div>
          );
          })}
        </div>
      )}

      {/* No Projects Found State */}
      {!loading && filteredProjects.length === 0 && (
        <div className={cardClass}>
          <div className="p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 className={`mt-4 text-lg font-medium ${textPrimary}`}>{t('list.none_found')}</h3>
            <p className={`mt-2 ${textSecondary}`}>
              {searchTerm || statusFilter !== 'All Status'
                ? 'Try adjusting your search or filters'
                : 'Get started by creating a new project'}
            </p>
            {(user?.role === userRoles.ADMIN || user?.role === userRoles.PROJECT_MANAGER) && (
              <button
                onClick={handleOpenNewProjectModal}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors mt-4 mx-auto"
              >
                <Plus className="w-5 h-5" />
                Create New Project
              </button>
            )}
          </div>
        </div>
      )}

      {/* render the Modal */}
      <ProjectModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSubParentId(null) }}
        editingProject={editingProject}
        initialParentId={subParentId}
        users={members}
        currentUser={user}
        onSubmit={handleCreateProject}
        presetTemplate={presetTemplate}
        templateAlreadyOffered
      />

      {/* The choice itself. Deliberately plain: two routes, and the templates listed under the
          second so picking one is a single click rather than a screen of its own. */}
      {chooserOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setChooserOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <div className="px-6 pt-6 pb-2">
              <h2 className="text-xl font-bold text-gray-900">Start a new project</h2>
              <p className="text-sm text-gray-500 mt-1">Two ways in — both open the same form.</p>
            </div>

            <div className="px-6 py-4 space-y-3">
              <button onClick={startBlank}
                      className="w-full text-left rounded-xl border border-gray-200 hover:border-blue-400 hover:bg-blue-50 px-4 py-3 transition">
                <span className="block text-sm font-semibold text-gray-900">Start from scratch</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  An empty form. You can still upload a TOR inside it to fill everything in.
                </span>
              </button>

              <div className="rounded-xl border border-gray-200">
                <div className="px-4 pt-3 pb-1">
                  <span className="block text-sm font-semibold text-gray-900">Start from a template</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    The stages this kind of work usually goes through. You still set the dates and
                    the money.
                  </span>
                </div>
                {templates.length === 0 ? (
                  <p className="px-4 py-3 text-xs text-gray-400">
                    No templates yet. An Admin or Project Manager can write one under Templates.
                  </p>
                ) : (
                  <ul className="p-2 space-y-1">
                    {templates.map(t => (
                      <li key={t.id}>
                        <button onClick={() => startFromTemplate(t)}
                                className="w-full text-left rounded-lg px-3 py-2 hover:bg-gray-50 transition">
                          <span className="block text-sm text-gray-900">{t.name}</span>
                          <span className="block text-xs text-gray-500">
                            {(t.milestones || []).length} stage{(t.milestones || []).length === 1 ? '' : 's'}
                            {t.description ? ` · ${t.description}` : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="px-6 pb-6">
              <button onClick={() => setChooserOpen(false)}
                      className="text-sm text-gray-500 hover:text-gray-800">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectList
